import { useMutation } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { RewardStatus } from "@/api/types";
import { queryClient } from "@/pages/Root";
import { get, post } from "api";

const checkin = debounce(
  (scannedCode: string) => post("/visit/checkin", { scannedCode }),
  500,
  { leading: true, trailing: false }
);

interface UseCheckinOptions {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

const getRewardProgress = async () => {
  const rewards = await queryClient.fetchQuery(["/rewards"], () =>
    get("/rewards", {})
  );

  const futureRewards = rewards.filter(
    (r) => r.status === RewardStatus.NotAvailable
  );

  const nextReward = futureRewards.length
    ? futureRewards.reduce(
        (acc, curr) =>
          acc.minimumVisits ?? 0 <= (curr.minimumVisits ?? 0) ? acc : curr,
        futureRewards[0]
      )
    : undefined;
  return nextReward?.unlockProgressPercent || 0;
};

const useCheckin = (options: UseCheckinOptions = {}) => {
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: checkin,
    onSuccess: async () => {
      // Default success behavior - navigate to reward checkout
      const rewardProgress = await getRewardProgress();
      const queryString = new URLSearchParams({
        rewardProgress: rewardProgress.toString(),
        comingFromScan: "true",
      }).toString();
      navigate(`/cart/checkout/reward?${queryString}`);

      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(); // Default reward progress, can be overridden
      }
    },
    onError: (err: any) => {
      // Call custom onError if provided
      if (options.onError) {
        options.onError(err);
      }
    },
  });

  const checkinUser = useCallback(
    (scannedCode: string) => {
      mutation.mutate(scannedCode);
    },
    [mutation]
  );

  return {
    checkinUser,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
  };
};

export default useCheckin;
